import React, { useState } from 'react';
import { Alert, Button, CircularProgress, Typography } from '@mui/material';

import StandardDialog, { StandardDialogActions } from '../../Modals/StandardDialog';
import { rerunScreening } from '../../../services/screening';

interface RescanCertificateDialogProps {
  isOpen: boolean;
  companyCertificationId: number | null;
  /** For the dialog copy — which certificate is about to be rescanned. */
  label: string;
  /** True when this certificate has never been screened, which changes the wording. */
  isFirstScan: boolean;
  handleClose: () => void;
  onQueued: () => void;
}

export default function RescanCertificateDialog({
  isOpen,
  companyCertificationId,
  label,
  isFirstScan,
  handleClose,
  onQueued,
}: RescanCertificateDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const title = isFirstScan ? 'Screen Certificate' : 'Re-screen Certificate';

  const handleConfirm = async () => {
    if (!companyCertificationId) return;
    setSubmitting(true);
    setError(null);
    try {
      await rerunScreening(companyCertificationId);
      onQueued();
      handleClose();
    } catch (err) {
      console.error('Error dispatching certificate screening', err);
      setError('Could not queue the screening. Confirm the certificate has a document attached and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StandardDialog isOpen={isOpen} handleClose={handleClose} title={title} maxWidth="sm">
      <Typography variant="body1" sx={{ mb: 2 }}>
        {isFirstScan
          ? `Run an automated screening of the certificate document for ${label}?`
          : `Run a new automated screening of the certificate document for ${label}? `
            + 'The current results and any accept/dismiss responses on them will be superseded.'}
      </Typography>

      <Alert severity="info">
        Screening runs in the background and takes about a minute. Reload the grid to see the result.
      </Alert>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      <StandardDialogActions>
        <Button variant="outlined" onClick={handleClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={submitting || !companyCertificationId}>
          {submitting ? <CircularProgress size={20} /> : title}
        </Button>
      </StandardDialogActions>
    </StandardDialog>
  );
}
